import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Search, UserPlus, Building2, X } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { GuestDonor, Office, UpdateGuestDonorData } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { useGuestDonors, useOffices, useUpdateGuestDonor, useDeleteGuestDonor } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const ManageGuestDonors: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const limit = 50;
  
  // Use React Query hooks for cached data with pagination
  const { data: guestDonorsResponse, isLoading: guestDonorsLoading, refetch: refetchGuestDonors } = useGuestDonors({ 
    page, 
    limit, 
    search: searchTerm || undefined,
    officeId: officeFilter || undefined
  });
  const { data: officesResponse, isLoading: officesLoading } = useOffices();
  
  const guestDonors = Array.isArray(guestDonorsResponse?.data) ? guestDonorsResponse.data : [];
  const pagination = guestDonorsResponse?.pagination;
  const offices = Array.isArray(officesResponse?.data)
    ? officesResponse.data
    : (Array.isArray(officesResponse) ? officesResponse : []);
  const loading = guestDonorsLoading || officesLoading;
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, officeFilter]);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingGuestDonor, setEditingGuestDonor] = useState<GuestDonor | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteGuestDonorId, setDeleteGuestDonorId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateGuestDonorData>({});
  const [editErrors, setEditErrors] = useState<{[key: string]: string}>({});
  const [showOfficeDropdown, setShowOfficeDropdown] = useState(false);
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');

  // Mutations
  const updateGuestDonorMutation = useUpdateGuestDonor({
    onSuccess: () => {
      toast.success('Guest donor updated successfully!');
      setShowEditForm(false);
      setEditingGuestDonor(null);
      refetchGuestDonors();
    },
    onError: (error: any) => {
      console.error('Error updating guest donor:', error);
      toast.error(getErrorMessage(error, 'Failed to update guest donor'));
    }
  });

  const deleteGuestDonorMutation = useDeleteGuestDonor({
    onSuccess: () => {
      toast.success('Guest donor deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteGuestDonorId(null);
      refetchGuestDonors();
    },
    onError: (error: any) => {
      console.error('Error deleting guest donor:', error);
      const message = getErrorMessage(error, 'Failed to delete guest donor');
      toast.error(message);
      setShowDeleteConfirm(false);
      setDeleteGuestDonorId(null);
    }
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOfficeDropdown) {
        setShowOfficeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOfficeDropdown]);


  const handleEdit = (guestDonor: GuestDonor) => {
    setEditingGuestDonor(guestDonor);
    setEditFormData({
      name: guestDonor.name,
      fatherOrHusbandName: guestDonor.fatherOrHusbandName,
      relation: guestDonor.relation,
      phone: guestDonor.phone,
      email: guestDonor.email,
      cnic: guestDonor.cnic,
      officeId: typeof guestDonor.officeId === 'string' ? guestDonor.officeId : guestDonor.officeId._id
    });
    setOfficeSearchTerm(
      typeof guestDonor.officeId === 'string' 
        ? offices.find(o => o._id === guestDonor.officeId)?.name || ''
        : guestDonor.officeId.name
    );
    setShowEditForm(true);
    setEditErrors({});
  };

  const handleUpdate = async () => {
    if (!editingGuestDonor) return;

    const newErrors: {[key: string]: string} = {};
    if (editFormData.phone && !/^\+92-\d{10}$/.test(editFormData.phone)) {
      newErrors.phone = 'Phone must be in format +92-XXXXXXXXXX';
    }
    if (editFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (editFormData.cnic && !/^\d{5}-\d{7}-\d$/.test(editFormData.cnic)) {
      newErrors.cnic = 'CNIC must be in format XXXXX-XXXXXXX-X';
    }
    if (!editFormData.officeId) {
      newErrors.officeId = 'Office is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setEditErrors(newErrors);
      return;
    }

    updateGuestDonorMutation.mutate({
      id: editingGuestDonor._id,
      data: editFormData
    });
  };

  const handleDelete = (id: string) => {
    setDeleteGuestDonorId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deleteGuestDonorId) return;
    deleteGuestDonorMutation.mutate(deleteGuestDonorId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteGuestDonorId(null);
  };


  const filteredOffices = offices.filter(office =>
    office.name.toLowerCase().includes(officeSearchTerm.toLowerCase()) ||
    office.officeNo.toString().includes(officeSearchTerm)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Guest Donors</h1>
        <p className="text-gray-600 dark:text-gray-400">View, edit, and manage guest donors</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <UserPlus className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Guest Donors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pagination?.total || guestDonors.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Distinct Offices</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {(() => {
                  const ids = new Set<string>();
                  for (const gd of guestDonors) {
                    const id = typeof gd.officeId === 'string' ? gd.officeId : gd.officeId?._id;
                    if (id) ids.add(String(id));
                  }
                  return ids.size;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search guest donors by ID, name, phone, email, or CNIC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={officeFilter}
            onChange={(e) => setOfficeFilter(e.target.value)}
            className="form-select md:col-span-1"
          >
            <option value="">All offices</option>
            {offices.map((office) => (
              <option key={office._id} value={office._id}>
                {office.name} (Office #{office.officeNo})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Guest Donors Table */}
      <div className="card">
        {guestDonors.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No guest donors found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first guest donor.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Guest ID</TH>
                    <TH>Name</TH>
                    <TH>Phone</TH>
                    <TH>Email</TH>
                    <TH>Office</TH>
                    <TH>Created Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {guestDonors.map((guestDonor) => (
                    <TR key={guestDonor._id}>
                      <TD className="font-medium">{guestDonor.guestId}</TD>
                      <TD>
                        <div>
                          <div className="font-medium">{guestDonor.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {guestDonor.fatherOrHusbandName} ({guestDonor.relation})
                          </div>
                        </div>
                      </TD>
                      <TD>{guestDonor.phone}</TD>
                      <TD>{guestDonor.email || '-'}</TD>
                      <TD>
                        {typeof guestDonor.officeId === 'object' ? (
                          <div>
                            <div className="font-medium">{guestDonor.officeId.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Office #{guestDonor.officeId.officeNo}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Loading...</span>
                        )}
                      </TD>
                      <TD>{new Date(guestDonor.createdAt).toLocaleDateString()}</TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(guestDonor)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(guestDonor._id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} guest donors
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={pagination.page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page === pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && editingGuestDonor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Edit Guest Donor: {editingGuestDonor.name}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Guest Donor Name</label>
                  <Input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Enter guest donor's full name"
                  />
                </div>

                <div>
                  <label className="form-label">Father/Husband Name</label>
                  <Input
                    type="text"
                    value={editFormData.fatherOrHusbandName || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, fatherOrHusbandName: e.target.value })}
                    placeholder="Enter father or husband name"
                  />
                </div>

                <div>
                  <label className="form-label">Relation</label>
                  <select
                    value={editFormData.relation || 'Father'}
                    onChange={(e) => setEditFormData({ ...editFormData, relation: e.target.value as 'Father' | 'Husband' })}
                    className="form-select"
                  >
                    <option value="Father">Father</option>
                    <option value="Husband">Husband</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Phone Number</label>
                  <Input
                    type="text"
                    value={editFormData.phone || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    placeholder="Add number without 0, e.g. 3331112223"
                    maxLength={14}
                    className={editErrors.phone ? 'border-red-500' : ''}
                  />
                  {editErrors.phone && <p className="text-red-500 text-xs mt-1">{editErrors.phone}</p>}
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <Input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    placeholder="Enter email address"
                    className={editErrors.email ? 'border-red-500' : ''}
                  />
                  {editErrors.email && <p className="text-red-500 text-xs mt-1">{editErrors.email}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="form-label">Office *</label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search offices..."
                      value={officeSearchTerm}
                      onChange={(e) => {
                        setOfficeSearchTerm(e.target.value);
                        setShowOfficeDropdown(true);
                      }}
                      onFocus={() => setShowOfficeDropdown(true)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={editErrors.officeId ? 'border-red-500' : ''}
                    />

                    {showOfficeDropdown && (
                      <div
                        className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {filteredOffices.map((office) => (
                          <div
                            key={office._id}
                            className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditFormData({ ...editFormData, officeId: office._id });
                              setOfficeSearchTerm(`${office.name} (Office #${office.officeNo})`);
                              setShowOfficeDropdown(false);
                              if (editErrors.officeId) setEditErrors({ ...editErrors, officeId: '' });
                            }}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {office.name} - Office #{office.officeNo}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {office.isCentral ? 'Central Office' : 'Regional Office'}
                            </div>
                          </div>
                        ))}
                        {filteredOffices.length === 0 && (
                          <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                            {officeSearchTerm ? 'No offices found' : 'Type to search offices...'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {editFormData.officeId && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Selected Office: <span className="font-medium text-gray-900 dark:text-gray-100">
                        {offices.find(o => o._id === editFormData.officeId)?.name || 'Unknown'}
                      </span>
                    </div>
                  )}

                  {editErrors.officeId && <p className="text-red-500 text-xs mt-1">{editErrors.officeId}</p>}
                </div>

              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button onClick={handleUpdate} className="flex-1">
                  Update Guest Donor
                </Button>
                <Button variant="outline" onClick={() => setShowEditForm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this guest donor? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={confirmDelete} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageGuestDonors;
